'use client';

import clsx from 'clsx';

import { useState } from 'react';

import { PlayOutline } from '@carbon/icons-react';
import { Column, Grid } from '@carbon/react';

import { ContentCard } from '@/components/ContentCard';
import type { Props as InteractiveLinkProps } from '@/components/InteractiveLink';
import { VideoPlayerModal } from '@/components/VideoPlayerModal';
import type { CaseStudy } from '@/types/CaseStudies';
import type { HeadingLevel } from '@/types/HeadingLevel';

type CaseStudiesGridProps = {
  data: CaseStudy[];
  className?: string;
  cardHeadingLevel?: HeadingLevel;
};

export function CaseStudiesGrid(props: CaseStudiesGridProps) {
  const { data, className, cardHeadingLevel = 'h3' } = props;
  const [modalVideoId, setModalVideoId] = useState<string | undefined>();
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  function toggleModal(id?: string): void {
    setModalVideoId(id);
    setIsVideoModalOpen(!isVideoModalOpen);
  }

  return (
    <>
      <Grid className={clsx('gap-y-32', className)} as="ul">
        {data.map(({ title, copy, videoId, storyUrl, image }) => {
          const videoLink: InteractiveLinkProps = {
            label: 'Watch the case study',
            url: '#',

            onClick: (e: any) => {
              e.preventDefault();
              toggleModal(videoId);
            },
            customIcon: <PlayOutline size={20} />,
            'data-autotrack-cta': `Watch the case study: ${title}_${videoId}`,
          };

          const storyLink: InteractiveLinkProps = {
            label: 'Read the story',
            url: storyUrl,
            'data-autotrack-cta': `Read the story: ${title}`,
          };

          const primaryLink = videoId ? videoLink : storyLink;
          const secondaryLink = videoId ? storyLink : null;

          return (
            <Column key={title} sm={4} as="li">
              <ContentCard
                image={image}
                title={title}
                copy={copy}
                link={primaryLink}
                secondaryLink={secondaryLink}
                headingLevel={cardHeadingLevel}
                as="article"
              />
            </Column>
          );
        })}
      </Grid>

      {isVideoModalOpen && modalVideoId && (
        <VideoPlayerModal videoId={modalVideoId} isOpen={isVideoModalOpen} onClose={toggleModal} />
      )}
    </>
  );
}
